import { Calendar, Clock, Moon, Save } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { useLanguage } from "../../context/LanguageContext";
import { islamicDatesAPI } from "../../lib/api";
import AdminLayout from "./AdminLayout";

const AdminSettingsPage = () => {
  const { t, lang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dates, setDates] = useState({
    hijri_year: "",
    eid_al_fitr_start: "",
    eid_al_fitr_end: "",
    eid_al_adha_start: "",
    eid_al_adha_end: "",
    muharram: "",
    mawlid: "",
    ramadan_start: "",
    ramadan_end: "",
    // Special prayer times
    friday_khutba: "13:15",
    friday_prayer: "13:45",
    eid_fitr_prayer: "08:00",
    eid_adha_prayer: "08:00",
    show_friday_prayer: true,
    show_ramadan: true,
    show_eid_al_fitr: true,
    show_eid_al_adha: true,
    show_friday_khutba: true,
    show_friday_prayer_time: true,
    show_eid_fitr_prayer: true,
    show_eid_adha_prayer: true,
    show_hijri_year: true,
  });

  const labels = {
    ro: {
      settings: "Setări",
      islamicDates: "Date islamice & Orare speciale",
      specialPrayers: "Orare de rugăciune speciale",
      fridayPrayer: "Rugăciunea de vineri",
      khutba: "Predica (Khutba)",
      prayer: "Rugăciunea",
      eidFitrPrayer: "Rugăciunea Eid al-Fitr",
      eidAdhaPrayer: "Rugăciunea Eid al-Adha",
      visibleOnSite: "Vizibil pe site",
      hijriYear: "Anul Hijri curent",
      startDate: "Data început",
      endDate: "Data sfârșit",
      otherDates: "Alte date importante",
      muharram: "1 Muharram (Anul Nou Islamic)",
      mawlid: "Mawlid an-Nabi (Ziua Profetului)",
      note: "Notă: Aceste date și orare sunt afișate pe site-ul public. Actualizați-le la începutul fiecărui an islamic.",
      saved: "Setări salvate cu succes",
      error: "Eroare la salvare",
    },
    en: {
      settings: "Settings",
      islamicDates: "Islamic Dates & Special Times",
      specialPrayers: "Special Prayer Times",
      fridayPrayer: "Friday Prayer",
      khutba: "Sermon (Khutba)",
      prayer: "Prayer",
      eidFitrPrayer: "Eid al-Fitr Prayer",
      eidAdhaPrayer: "Eid al-Adha Prayer",
      visibleOnSite: "Visible on site",
      hijriYear: "Current Hijri Year",
      startDate: "Start Date",
      endDate: "End Date",
      otherDates: "Other Important Dates",
      muharram: "1st Muharram (Islamic New Year)",
      mawlid: "Mawlid an-Nabi (Prophet's Birthday)",
      note: "Note: These dates and times are displayed on the public website. Update them at the beginning of each Islamic year.",
      saved: "Settings saved successfully",
      error: "Error saving settings",
    },
    ar: {
      settings: "الإعدادات",
      islamicDates: "التواريخ الإسلامية والأوقات الخاصة",
      specialPrayers: "أوقات الصلاة الخاصة",
      fridayPrayer: "صلاة الجمعة",
      khutba: "الخطبة",
      prayer: "الصلاة",
      eidFitrPrayer: "صلاة عيد الفطر",
      eidAdhaPrayer: "صلاة عيد الأضحى",
      visibleOnSite: "مرئي على الموقع",
      hijriYear: "السنة الهجرية الحالية",
      startDate: "تاريخ البداية",
      endDate: "تاريخ النهاية",
      otherDates: "تواريخ مهمة أخرى",
      muharram: "1 محرم (رأس السنة الهجرية)",
      mawlid: "المولد النبوي الشريف",
      note: "ملاحظة: يتم عرض هذه التواريخ والأوقات على الموقع العام. قم بتحديثها في بداية كل سنة هجرية.",
      saved: "تم حفظ الإعدادات بنجاح",
      error: "خطأ في حفظ الإعدادات",
    },
  };

  const l = labels[lang] || labels.ro;

  const fetchDates = useCallback(async () => {
    try {
      const data = await islamicDatesAPI.get();
      setDates((prev) => ({
        ...prev,
        ...data,
      }));
    } catch (error) {
      toast.error(l.error);
    } finally {
      setLoading(false);
    }
  }, [l.error]);

  useEffect(() => {
    fetchDates();
  }, [fetchDates]);

  const handleChange = (field, value) => {
    setDates((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await islamicDatesAPI.update(dates);
      toast.success(l.saved);
    } catch (error) {
      toast.error(l.error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-stone-200 rounded w-1/4"></div>
          <div className="h-64 bg-stone-200 rounded"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-stone-800">
            {l.settings}
          </h1>
          <p className="text-stone-500 text-sm">{l.islamicDates}</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-islamic-teal hover:bg-islamic-teal-light gap-2"
          data-testid="save-dates-btn">
          <Save className="w-4 h-4" />
          {saving ? t("common.loading") : t("common.save")}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Special Prayer Times - NEW SECTION */}
        <Card className="border-islamic-teal border-2">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-islamic-teal/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-islamic-teal" />
              </div>
              <h2 className="font-semibold text-lg text-stone-800">
                {l.specialPrayers}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Friday Prayer */}
              <div className="p-4 bg-stone-50 rounded-lg">
                <h3 className="font-medium text-stone-800 mb-4 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-islamic-teal" />
                  {l.fridayPrayer}
                </h3>
                <div className="flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-stone-200">
                  <Label
                    htmlFor="show_friday_prayer"
                    className="text-sm text-stone-700">
                    {l.visibleOnSite}
                  </Label>
                  <Switch
                    id="show_friday_prayer"
                    checked={!!dates.show_friday_prayer}
                    onCheckedChange={(checked) =>
                      handleChange("show_friday_prayer", checked)
                    }
                    data-testid="show-friday-prayer-switch"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="friday_khutba">{l.khutba}</Label>
                      <Switch
                        id="show_friday_khutba"
                        checked={!!dates.show_friday_khutba}
                        onCheckedChange={(checked) =>
                          handleChange("show_friday_khutba", checked)
                        }
                        data-testid="show-friday-khutba-switch"
                      />
                    </div>
                    <Input
                      id="friday_khutba"
                      type="time"
                      value={dates.friday_khutba || "13:15"}
                      onChange={(e) =>
                        handleChange("friday_khutba", e.target.value)
                      }
                      data-testid="friday-khutba"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="friday_prayer">{l.prayer}</Label>
                      <Switch
                        id="show_friday_prayer_time"
                        checked={!!dates.show_friday_prayer_time}
                        onCheckedChange={(checked) =>
                          handleChange("show_friday_prayer_time", checked)
                        }
                        data-testid="show-friday-prayer-time-switch"
                      />
                    </div>
                    <Input
                      id="friday_prayer"
                      type="time"
                      value={dates.friday_prayer || "13:45"}
                      onChange={(e) =>
                        handleChange("friday_prayer", e.target.value)
                      }
                      data-testid="friday-prayer"
                    />
                  </div>
                </div>
              </div>

              {/* Eid Prayers */}
              <div className="p-4 bg-stone-50 rounded-lg">
                <h3 className="font-medium text-stone-800 mb-4 flex items-center gap-2">
                  <span className="font-arabic text-islamic-gold">عيد</span>
                  {lang === "en"
                    ? "Eid Prayers"
                    : lang === "ar"
                    ? "صلاة العيد"
                    : "Rugăciunile de Eid"}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="eid_fitr_prayer">{l.eidFitrPrayer}</Label>
                      <Switch
                        id="show_eid_fitr_prayer"
                        checked={!!dates.show_eid_fitr_prayer}
                        onCheckedChange={(checked) =>
                          handleChange("show_eid_fitr_prayer", checked)
                        }
                        data-testid="show-eid-fitr-prayer-switch"
                      />
                    </div>
                    <Input
                      id="eid_fitr_prayer"
                      type="time"
                      value={dates.eid_fitr_prayer || "08:00"}
                      onChange={(e) =>
                        handleChange("eid_fitr_prayer", e.target.value)
                      }
                      data-testid="eid-fitr-prayer"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="eid_adha_prayer">{l.eidAdhaPrayer}</Label>
                      <Switch
                        id="show_eid_adha_prayer"
                        checked={!!dates.show_eid_adha_prayer}
                        onCheckedChange={(checked) =>
                          handleChange("show_eid_adha_prayer", checked)
                        }
                        data-testid="show-eid-adha-prayer-switch"
                      />
                    </div>
                    <Input
                      id="eid_adha_prayer"
                      type="time"
                      value={dates.eid_adha_prayer || "08:00"}
                      onChange={(e) =>
                        handleChange("eid_adha_prayer", e.target.value)
                      }
                      data-testid="eid-adha-prayer"
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Hijri Year */}
          <Card className="border-stone-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-islamic-teal/10 flex items-center justify-center">
                  <Moon className="w-5 h-5 text-islamic-teal" />
                </div>
                <h2 className="font-semibold text-stone-800">{l.hijriYear}</h2>
              </div>
              <div className="flex items-center justify-between mb-4 p-3 bg-stone-50 rounded-lg border border-stone-200">
                <Label
                  htmlFor="show_hijri_year"
                  className="text-sm text-stone-700">
                  {l.visibleOnSite}
                </Label>
                <Switch
                  id="show_hijri_year"
                  checked={!!dates.show_hijri_year}
                  onCheckedChange={(checked) =>
                    handleChange("show_hijri_year", checked)
                  }
                  data-testid="show-hijri-year-switch"
                />
              </div>
              <div>
                <Label htmlFor="hijri_year">{l.hijriYear}</Label>
                <Input
                  id="hijri_year"
                  value={dates.hijri_year || ""}
                  onChange={(e) => handleChange("hijri_year", e.target.value)}
                  placeholder="1446"
                  data-testid="hijri-year"
                />
              </div>
            </CardContent>
          </Card>

          {/* Ramadan */}
          <Card className="border-stone-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-islamic-gold/10 flex items-center justify-center">
                  <span className="text-xl">🌙</span>
                </div>
                <h2 className="font-semibold text-stone-800">Ramadan</h2>
              </div>
              <div className="flex items-center justify-between mb-4 p-3 bg-stone-50 rounded-lg border border-stone-200">
                <Label
                  htmlFor="show_ramadan"
                  className="text-sm text-stone-700">
                  {l.visibleOnSite}
                </Label>
                <Switch
                  id="show_ramadan"
                  checked={!!dates.show_ramadan}
                  onCheckedChange={(checked) =>
                    handleChange("show_ramadan", checked)
                  }
                  data-testid="show-ramadan-switch"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ramadan_start">{l.startDate}</Label>
                  <Input
                    id="ramadan_start"
                    type="date"
                    value={dates.ramadan_start || ""}
                    onChange={(e) =>
                      handleChange("ramadan_start", e.target.value)
                    }
                    data-testid="ramadan-start"
                  />
                </div>
                <div>
                  <Label htmlFor="ramadan_end">{l.endDate}</Label>
                  <Input
                    id="ramadan_end"
                    type="date"
                    value={dates.ramadan_end || ""}
                    onChange={(e) =>
                      handleChange("ramadan_end", e.target.value)
                    }
                    data-testid="ramadan-end"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Eid al-Fitr */}
          <Card className="border-stone-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                  <span className="text-xl">🎉</span>
                </div>
                <h2 className="font-semibold text-stone-800">Eid al-Fitr</h2>
              </div>
              <div className="flex items-center justify-between mb-4 p-3 bg-stone-50 rounded-lg border border-stone-200">
                <Label
                  htmlFor="show_eid_al_fitr"
                  className="text-sm text-stone-700">
                  {l.visibleOnSite}
                </Label>
                <Switch
                  id="show_eid_al_fitr"
                  checked={!!dates.show_eid_al_fitr}
                  onCheckedChange={(checked) =>
                    handleChange("show_eid_al_fitr", checked)
                  }
                  data-testid="show-eid-fitr-switch"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="eid_al_fitr_start">{l.startDate}</Label>
                  <Input
                    id="eid_al_fitr_start"
                    type="date"
                    value={dates.eid_al_fitr_start || ""}
                    onChange={(e) =>
                      handleChange("eid_al_fitr_start", e.target.value)
                    }
                    data-testid="eid-fitr-start"
                  />
                </div>
                <div>
                  <Label htmlFor="eid_al_fitr_end">{l.endDate}</Label>
                  <Input
                    id="eid_al_fitr_end"
                    type="date"
                    value={dates.eid_al_fitr_end || ""}
                    onChange={(e) =>
                      handleChange("eid_al_fitr_end", e.target.value)
                    }
                    data-testid="eid-fitr-end"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Eid al-Adha */}
          <Card className="border-stone-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <span className="text-xl">🐑</span>
                </div>
                <h2 className="font-semibold text-stone-800">Eid al-Adha</h2>
              </div>
              <div className="flex items-center justify-between mb-4 p-3 bg-stone-50 rounded-lg border border-stone-200">
                <Label
                  htmlFor="show_eid_al_adha"
                  className="text-sm text-stone-700">
                  {l.visibleOnSite}
                </Label>
                <Switch
                  id="show_eid_al_adha"
                  checked={!!dates.show_eid_al_adha}
                  onCheckedChange={(checked) =>
                    handleChange("show_eid_al_adha", checked)
                  }
                  data-testid="show-eid-adha-switch"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="eid_al_adha_start">{l.startDate}</Label>
                  <Input
                    id="eid_al_adha_start"
                    type="date"
                    value={dates.eid_al_adha_start || ""}
                    onChange={(e) =>
                      handleChange("eid_al_adha_start", e.target.value)
                    }
                    data-testid="eid-adha-start"
                  />
                </div>
                <div>
                  <Label htmlFor="eid_al_adha_end">{l.endDate}</Label>
                  <Input
                    id="eid_al_adha_end"
                    type="date"
                    value={dates.eid_al_adha_end || ""}
                    onChange={(e) =>
                      handleChange("eid_al_adha_end", e.target.value)
                    }
                    data-testid="eid-adha-end"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Other Important Dates */}
          <Card className="border-stone-200 lg:col-span-2">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-stone-600" />
                </div>
                <h2 className="font-semibold text-stone-800">{l.otherDates}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="muharram">{l.muharram}</Label>
                  <Input
                    id="muharram"
                    type="date"
                    value={dates.muharram || ""}
                    onChange={(e) => handleChange("muharram", e.target.value)}
                    data-testid="muharram"
                  />
                </div>
                <div>
                  <Label htmlFor="mawlid">{l.mawlid}</Label>
                  <Input
                    id="mawlid"
                    type="date"
                    value={dates.mawlid || ""}
                    onChange={(e) => handleChange("mawlid", e.target.value)}
                    data-testid="mawlid"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info */}
        <div className="p-4 bg-islamic-teal/5 rounded-lg border border-islamic-teal/20">
          <p className="text-sm text-stone-600">
            <strong>📌</strong> {l.note}
          </p>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
